/**
 * PackerPriorityBadge - Packer guidance based on live bin occupancy
 *
 * Tells warehouse packers what to work on next based on how full the bins are:
 * - SINGLE_LINE (<30% full): Bins are empty — prioritise quick single-item orders
 * - EITHER (30–69%): Normal — pack whatever's next
 * - CLEAR_BINS (>=70%): Bins backing up — clear multi-item bins to free space
 *
 * Colour note: EITHER uses amber-600 (not yellow-500) for WCAG AA contrast on white text.
 */
import { Package, Box, ArrowLeftRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export type PackerPriority = 'SINGLE_LINE' | 'CLEAR_BINS' | 'EITHER';

export interface PackerPriorityBadgeProps {
  priority: PackerPriority;
  occupancyPercent: number;
  className?: string;
}

const PRIORITY_CONFIG = {
  SINGLE_LINE: {
    labelKey: 'bins.packerPriority.singleLine',
    messageKey: 'bins.packerPriority.singleLineMsg',
    bg: 'bg-green-50 dark:bg-green-950/40',
    border: 'border-green-200 dark:border-green-800',
    accent: 'text-green-700 dark:text-green-400',
    muted: 'text-green-600/80 dark:text-green-400/70',
    dot: 'bg-green-500',
    icon: Package,
  },
  CLEAR_BINS: {
    labelKey: 'bins.packerPriority.clearBins',
    messageKey: 'bins.packerPriority.clearBinsMsg',
    bg: 'bg-red-50 dark:bg-red-950/40',
    border: 'border-red-200 dark:border-red-800',
    accent: 'text-red-700 dark:text-red-400',
    muted: 'text-red-600/80 dark:text-red-400/70',
    dot: 'bg-red-500',
    icon: Box,
  },
  EITHER: {
    labelKey: 'bins.packerPriority.either',
    messageKey: 'bins.packerPriority.eitherMsg',
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-800',
    accent: 'text-amber-700 dark:text-amber-400',
    muted: 'text-amber-600/80 dark:text-amber-400/70',
    dot: 'bg-amber-500',
    icon: ArrowLeftRight,
  },
} as const;

export function PackerPriorityBadge({
  priority,
  occupancyPercent,
  className,
}: PackerPriorityBadgeProps) {
  const { t } = useTranslation('production');
  const config = PRIORITY_CONFIG[priority];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border px-5 py-3.5',
        config.bg,
        config.border,
        className,
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="flex items-center justify-between gap-4">
        {/* Left: priority guidance */}
        <div className="flex items-center gap-3 min-w-0">
          <div className={cn('flex-shrink-0 rounded-md p-2', config.bg)}>
            <Icon className={cn('h-5 w-5', config.accent)} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn('text-xs font-medium uppercase tracking-wide', config.muted)}>
                {t('bins.packerPriority')}
              </span>
              <span className={cn('h-1.5 w-1.5 rounded-full', config.dot)} />
            </div>
            <p className={cn('font-semibold', config.accent)}>{t(config.labelKey)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t(config.messageKey)}</p>
          </div>
        </div>

        {/* Right: occupancy */}
        <div className="flex-shrink-0 text-right">
          <p className={cn('text-2xl font-bold tabular-nums leading-none', config.accent)}>
            {occupancyPercent}%
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{t('bins.binsFull')}</p>
        </div>
      </div>
    </div>
  );
}
